/**
 * US-158: CCPA & US Compliance Scanning Dashboard
 * Task: T-158-002
 *
 * Dashboard UI component:
 * - Shows overall compliance status (pass/fail/warn badge)
 * - Lists violations in a table (sorted by severity: CRITICAL -> HIGH -> MEDIUM -> LOW)
 * - Shows residency compliance indicator
 * - "Run Scan" button triggers POST /check and refreshes
 * - Loading and error states with data-testid attributes
 *
 * Testids:
 * - scanner-dashboard
 * - scan-status-badge
 * - violation-row
 * - run-scan-btn
 * - residency-status
 * - scanner-loading
 * - scanner-error
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ScanResult,
  Violation,
  runComplianceScan,
  getLatestSnapshot,
} from './engine';
import { BreachNotificationTracker } from './BreachNotificationTracker';
import { ClassifiedHttpError, classifyHttpError } from '../../services/httpError';

const SEVERITY_ORDER: Record<string, number> = {
  CRITICAL: 1,
  HIGH: 2,
  MEDIUM: 3,
  LOW: 4,
};

export interface ScannerDashboardProps {
  initialData?: ScanResult | null;
  autoFetch?: boolean;
}

export const ScannerDashboard: React.FC<ScannerDashboardProps> = ({
  initialData = null,
  autoFetch = true,
}) => {
  const [scanResult, setScanResult] = useState<ScanResult | null>(initialData);
  const [loading, setLoading] = useState<boolean>(!initialData && autoFetch);
  const [scanning, setScanning] = useState<boolean>(false);
  // UX-11: the classified transport error, surfaced as a VALIDATION notice
  // (request-shape failure) rather than a red fault — never as a violation.
  const [error, setError] = useState<ClassifiedHttpError | null>(null);

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const snapshot = await getLatestSnapshot();
      if (snapshot) {
        setScanResult(snapshot);
      } else {
        const fresh = await runComplianceScan();
        setScanResult(fresh);
      }
    } catch (err: any) {
      setError(classifyHttpError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initialData && autoFetch) {
      fetchInitialData();
    }
  }, [initialData, autoFetch, fetchInitialData]);

  const handleRunScan = async () => {
    try {
      setScanning(true);
      setError(null);
      const result = await runComplianceScan();
      setScanResult(result);
    } catch (err: any) {
      setError(classifyHttpError(err));
    } finally {
      setScanning(false);
    }
  };

  // Sort violations by severity: CRITICAL -> HIGH -> MEDIUM -> LOW
  const sortedViolations = useMemo(() => {
    if (!scanResult?.violations) return [];
    return [...scanResult.violations].sort((a, b) => {
      const orderA = SEVERITY_ORDER[a.severity] ?? 99;
      const orderB = SEVERITY_ORDER[b.severity] ?? 99;
      return orderA - orderB;
    });
  }, [scanResult?.violations]);

  const statusBadgeConfig = {
    PASS: {
      label: 'PASS',
      classes: 'bg-emerald-900/60 text-emerald-300 border-emerald-500/40',
      dot: 'bg-emerald-400',
    },
    WARN: {
      label: 'WARN',
      classes: 'bg-amber-900/60 text-amber-300 border-amber-500/40',
      dot: 'bg-amber-400',
    },
    FAIL: {
      label: 'FAIL',
      classes: 'bg-rose-900/60 text-rose-300 border-rose-500/40',
      dot: 'bg-rose-400',
    },
  };

  const currentStatus = scanResult?.status ?? 'WARN';
  const badgeConfig = statusBadgeConfig[currentStatus] || statusBadgeConfig.WARN;

  return (
    <div
      data-testid="scanner-dashboard"
      className="scanner-dashboard min-h-screen bg-slate-950 text-slate-100 p-6 md:p-8 space-y-6"
    >
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-white">
              CCPA & US Compliance Scanner
            </h1>
            {scanResult && (
              <span
                data-testid="scan-status-badge"
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border tracking-wider uppercase ${badgeConfig.classes}`}
              >
                <span className={`w-2 h-2 rounded-full ${badgeConfig.dot}`} />
                {badgeConfig.label}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Continuous compliance evaluation for CCPA, CPRA, and Texas data protection mandates.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="run-scan-btn"
            onClick={handleRunScan}
            disabled={loading || scanning}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 disabled:bg-slate-800 disabled:text-slate-500 font-medium text-sm text-white shadow-lg shadow-indigo-900/20 transition-all flex items-center gap-2"
          >
            {scanning ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span>Scanning...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Run Scan</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Loading state indicator */}
      {loading && (
        <div
          data-testid="scanner-loading"
          className="p-8 text-center bg-slate-900/60 border border-slate-800 rounded-xl"
        >
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-500 border-t-transparent mb-3" />
          <p className="text-slate-300 font-medium">Evaluating compliance posture...</p>
          <p className="text-xs text-slate-500 mt-1">
            Analyzing CCPA snapshots, privacy rules, and data residency.
          </p>
        </div>
      )}

      {/* UX-11: a request-shape failure (HTTP 422/400) is a VALIDATION notice
          about the request — NOT a compliance error and NEVER a violation.
          Amber, not red; it never touches the violations table or counts. */}
      {error && error.isValidation && (
        <div
          data-testid="scanner-validation-notice"
          role="status"
          className="p-4 rounded-xl bg-amber-950/50 border border-amber-700 text-amber-100 text-sm flex items-start justify-between gap-3"
        >
          <div>
            <div className="font-semibold text-amber-200">
              Request could not be validated
            </div>
            <div className="text-xs text-amber-200/90 mt-0.5">{error.message}</div>
            {error.issues.length > 0 && (
              <ul className="text-xs text-amber-200/80 mt-2 list-disc list-inside space-y-0.5">
                {error.issues.map((issue, idx) => (
                  <li key={idx}>
                    {issue.field ? <span className="font-mono">{issue.field}: </span> : null}
                    {issue.message}
                  </li>
                ))}
              </ul>
            )}
            <div className="text-[11px] text-amber-200/60 mt-2">
              This is a validation response, not a compliance finding.
            </div>
          </div>
          <button
            onClick={() => handleRunScan()}
            className="text-xs underline text-amber-200 hover:text-white"
          >
            Retry Scan
          </button>
        </div>
      )}

      {/* Error state alert — genuine faults only (network/server/auth). */}
      {error && !error.isValidation && (
        <div
          data-testid="scanner-error"
          role="alert"
          className="p-4 rounded-xl bg-rose-950/60 border border-rose-800 text-rose-200 text-sm flex items-start justify-between gap-3"
        >
          <div>
            <div className="font-semibold text-rose-300">Compliance Scanner Error</div>
            <div className="text-xs text-rose-400 mt-0.5">{error.message}</div>
          </div>
          <button
            onClick={() => handleRunScan()}
            className="text-xs underline text-rose-300 hover:text-white"
          >
            Retry Scan
          </button>
        </div>
      )}

      {/* Main Dashboard Content */}
      {scanResult && !loading && (
        <>
          {/* Key Metrics / Residency Status Card */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Overall Status */}
            <div className="p-5 rounded-xl bg-slate-900/80 border border-slate-800">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Overall State
              </span>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-2xl font-bold text-white tracking-tight">
                  {scanResult.status}
                </span>
                <span className={`text-xs px-2.5 py-1 rounded-full border font-bold ${badgeConfig.classes}`}>
                  {scanResult.status === 'PASS'
                    ? '100% Compliant'
                    : scanResult.status === 'WARN'
                    ? 'Action Recommended'
                    : 'Remediation Required'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Last scanned: {new Date(scanResult.timestamp).toLocaleString()}
              </p>
            </div>

            {/* Residency Status Indicator */}
            <div
              data-testid="residency-status"
              className="p-5 rounded-xl bg-slate-900/80 border border-slate-800"
            >
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Data Residency
              </span>
              <div className="mt-2 flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-white tracking-tight">
                    {scanResult.residency?.region || 'US-TX'}
                  </div>
                  <div className="text-xs text-slate-400">
                    Scope: {scanResult.residency?.inScope ? 'In-Scope' : 'Out-of-Scope'}
                  </div>
                </div>
                <span
                  className={`text-xs px-2.5 py-1 rounded-full border font-bold ${
                    scanResult.residency?.compliant
                      ? 'bg-emerald-900/60 text-emerald-300 border-emerald-500/40'
                      : 'bg-rose-900/60 text-rose-300 border-rose-500/40'
                  }`}
                >
                  {scanResult.residency?.compliant ? 'Compliant' : 'Non-Compliant'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Restricted to US sovereign data boundary
              </p>
            </div>

            {/* Violations Counter */}
            <div className="p-5 rounded-xl bg-slate-900/80 border border-slate-800">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Active Violations
              </span>
              <div className="mt-2 flex items-center justify-between">
                <span className="text-2xl font-bold text-white tracking-tight">
                  {sortedViolations.length}
                </span>
                <div className="text-xs text-slate-400 flex gap-2">
                  <span className="text-rose-400">
                    {sortedViolations.filter((v) => v.severity === 'CRITICAL').length} Critical
                  </span>
                  <span className="text-amber-400">
                    {sortedViolations.filter((v) => v.severity === 'HIGH').length} High
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Sorted by statutory severity hierarchy
              </p>
            </div>
          </div>

          {/* Breach Notification Tracker */}
          <BreachNotificationTracker
            violations={scanResult.violations}
            onRefresh={handleRunScan}
          />

          {/* Violations Table */}
          <div className="rounded-xl bg-slate-900/80 border border-slate-800 overflow-hidden shadow-lg">
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">Detected Violations</h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Ordered by severity (Critical, High, Medium, Low)
                </p>
              </div>
              <span className="text-xs text-slate-500">
                Total: {sortedViolations.length} items
              </span>
            </div>

            {sortedViolations.length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-sm">
                <div className="inline-block p-3 rounded-full bg-emerald-950/40 text-emerald-400 mb-2 border border-emerald-800/40">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="font-medium text-slate-300">Clean Compliance Scan</p>
                <p className="text-xs text-slate-500 mt-1">
                  No active CCPA violations or statutory non-compliance items detected.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-300">
                  <thead className="bg-slate-950/60 text-xs uppercase text-slate-400 border-b border-slate-800">
                    <tr>
                      <th className="py-3 px-4">Severity</th>
                      <th className="py-3 px-4">Type</th>
                      <th className="py-3 px-4">Description</th>
                      <th className="py-3 px-4">Detected At</th>
                      <th className="py-3 px-4">Deadline</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {sortedViolations.map((violation) => (
                      <tr
                        key={violation.id}
                        data-testid="violation-row"
                        className="hover:bg-slate-800/40 transition-colors"
                      >
                        <td className="py-3 px-4 whitespace-nowrap">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold tracking-wider uppercase ${
                              violation.severity === 'CRITICAL'
                                ? 'bg-rose-950 text-rose-300 border border-rose-800'
                                : violation.severity === 'HIGH'
                                ? 'bg-amber-950 text-amber-300 border border-amber-800'
                                : violation.severity === 'MEDIUM'
                                ? 'bg-yellow-950 text-yellow-300 border border-yellow-800'
                                : 'bg-slate-800 text-slate-300 border border-slate-700'
                            }`}
                          >
                            {violation.severity}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-medium text-white whitespace-nowrap">
                          {violation.type}
                        </td>
                        <td className="py-3 px-4 text-slate-300 max-w-md">
                          {violation.description}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-400 whitespace-nowrap">
                          {new Date(violation.detectedAt).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4 text-xs whitespace-nowrap">
                          {violation.deadline ? (
                            <span className="text-amber-300 font-mono">
                              {new Date(violation.deadline).toLocaleDateString()}
                            </span>
                          ) : (
                            <span className="text-slate-600">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ScannerDashboard;
